import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Footer from '../home/components/Footer';

// SEO Component for terms page
function TermsSEO() {
  useEffect(() => {
    // Update page title
    document.title = '施工前の確認事項 | 岐阜県美濃加茂市洗車コーティング専門店 Garage de carcare';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岐阜県美濃加茂市の洗車コーティング専門店Garage de carcareの施工前確認事項。自家塗装、塗装劣化、ラッピング車両等の制限事項と免責事項をご確認ください。');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/terms`);
    }

    // Add Schema.org JSON-LD for FAQ
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "施工前の確認事項",
      "description": "岐阜県美濃加茂市の洗車コーティング専門店Garage de carcareの施工前確認事項",
      "url": `${import.meta.env.VITE_SITE_URL}/terms`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "自家塗装車両への施工は可能ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "塗膜の密着が弱く、研磨で塗装が剥がれる可能性があるため、施工をお断りまたは制限する場合があります。"
          }
        },
        {
          "@type": "Question",
          "name": "塗装劣化・クリア剥げ・サビ発生箇所がある車両への施工は可能ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "板金塗装をお勧めします。施工をお断りまたは制限する場合があります。"
          }
        },
        {
          "@type": "Question",
          "name": "ラッピング・マット塗装車両への施工は可能ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "通常のコーティング剤は使用できないため、施工をお断りまたは制限する場合があります。"
          }
        },
        {
          "@type": "Question",
          "name": "改造・特殊車両・旧車への施工は可能ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "塗膜が不明なため、テスト施工のうえで判断いたします。"
          }
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null;
}

// Header component for terms page
function TermsHeader() {
  const navigate = useNavigate();

  const handleNavigation = (section: string) => {
    if (section === 'home') {
      navigate('/');
    } else {
      navigate('/', { state: { scrollTo: section } });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => handleNavigation('home')}
              className="cursor-pointer"
            >
              <img 
                src="https://static.readdy.ai/image/18ddd3c5386ce7455627992c42f8b0cb/f4e0fe60f5ceffe9412c875da26f8438.jpeg"
                alt="ガレージ・ド・カルカレ ロゴ"
                className="h-12 w-auto object-contain"
              />
            </button>
          </div>

          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => handleNavigation('home')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              ホーム
            </button>
            <button 
              onClick={() => handleNavigation('about')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              概要
            </button>
            <button 
              onClick={() => handleNavigation('services')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              サービス・料金
            </button>
            <button 
              onClick={() => handleNavigation('gallery')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              施工実績
            </button>
            <button 
              onClick={() => handleNavigation('booking')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              予約する
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button className="text-gray-700 hover:text-blue-600">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <TermsSEO />
      <TermsHeader />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold mb-4">施工前の確認事項</h1>
            <p className="text-xl text-blue-100">
              ご予約前に必ずご確認ください
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Thank you message */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-blue-600 text-2xl"></i>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                この度は当店にご依頼いただき、誠にありがとうございます。<br />
                施工にあたり、以下の内容をご確認・ご了承をお願いしております。
              </p>
            </div>

            {/* 施工をお断りまたは制限する場合 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <i className="ri-alert-line text-red-500 mr-3"></i>
                施工をお断りまたは制限する場合
              </h2>
              
              <div className="space-y-6">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-800 mb-2">1. 自家塗装</h3>
                  <p className="text-gray-700">
                    → 塗膜の密着が弱く、研磨で塗装が剥がれる可能性があります。
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-800 mb-2">2. 塗装劣化・クリア剥げ・サビ発生箇所</h3>
                  <p className="text-gray-700">
                    → 板金塗装をお勧めします。
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-800 mb-2">3. ラッピング・マット塗装車両</h3>
                  <p className="text-gray-700">
                    → 通常のコーティング剤は使用できません。
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-800 mb-2">4. 改造・特殊車両・旧車（塗膜が不明なもの）</h3>
                  <p className="text-gray-700">
                    → テスト施工のうえで判断いたします。
                  </p>
                </div>
              </div>
            </div>

            {/* 免責事項 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <i className="ri-shield-check-line text-yellow-500 mr-3"></i>
                免責事項
              </h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span>上記車両への施工により塗装トラブルが発生した場合、当店では保証いたしかねます。</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span>施工後の管理・メンテナンス方法により、効果の持続期間は変動いたします。</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span>万が一の不具合については、48時間以内にご連絡をお願いいたします。</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/booking')}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                内容を確認して予約に進む
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line mr-2"></i>
                ホームに戻る
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}